<!-- src/shared/components/navigation/UserMenu.vue -->

<!--
  "Complete MFA Verification" is shown in the UserMenu when:

  Condition: awaitingMfa prop is true

  This happens during the partial authentication state where:

  1. User has successfully completed the first authentication factor (password or magic link)
  2. But still needs to complete the second factor (MFA/TOTP code)
  3. Session has awaiting_mfa = true in the backend state

  Visual indicators when awaitingMfa is true:
  - Avatar circle background changes to amber/yellow (instead of brand blue)
  - Small amber pulse badge appears on top-right of avatar
  - Menu shows prominent "Complete MFA Verification" link in amber text at the top
  - Regular menu items are hidden (only MFA verification link shown)
  - Links to /mfa-verify route

  This state occurs in full authentication mode when:
  - User logs in with password but has MFA enabled
  - User clicks magic link but has MFA enabled
  - Backend sets session['awaiting_mfa'] = true
  - Frontend receives awaiting_mfa: true via /bootstrap/me endpoint
-->

<script setup lang="ts">
  import FancyIcon from '@/shared/components/ctas/FancyIcon.vue';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import { useAuth } from '@/shared/composables/useAuth';
  import { useTheme } from '@/shared/composables/useTheme';
  import { useDomainContext } from '@/shared/composables/useDomainContext';
  import { usePreviewPlanMode } from '@/shared/composables/usePreviewPlanMode';
  import { useScopeSwitcherVisibility } from '@/shared/composables/useScopeSwitcherVisibility';
  import { type Customer } from '@/schemas/shapes/v3';
  import { ENTITLEMENTS } from '@/types/organization';
  import { isOrgsAuditLogsEnabled } from '@/utils/features';
  import { useAuthStore } from '@/shared/stores/authStore';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useProductIdentity } from '@/shared/stores/identityStore';
  import { useOrganizationStore } from '@/shared/stores/organizationStore';
  import PlanPreviewModal from '@/shared/components/modals/PlanPreviewModal.vue';
  import { storeToRefs } from 'pinia';
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  // Copy to clipboard helper
  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showCopyFeedback(fieldName);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showCopyFeedback(fieldName);
    }
  };

  const copiedField = ref<string | null>(null);
  const showCopyFeedback = (fieldName: string) => {
    copiedField.value = fieldName;
    setTimeout(() => {
      copiedField.value = null;
    }, 1500);
  };

  const props = defineProps<{
    cust: Customer | null;
    email?: string; // Used when awaiting MFA (no customer object yet)
    colonel?: boolean;
    awaitingMfa?: boolean;
  }>();

  const { t } = useI18n();
  const { logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();

  const isPlanPreviewModalOpen = ref(false);

  // Test plan mode composable
  const { isPreviewModeActive } = usePreviewPlanMode();

  const bootstrapStore = useBootstrapStore();
  const { billing_enabled } = storeToRefs(bootstrapStore);

  // Mutation controls in the chrome are gated while the coordinator cannot
  // vouch for the session (ADR-046#authority-action-gating). Escape actions (logout) stay
  // enabled: retained-identity states must still let the user leave.
  const authStore = useAuthStore();
  const { protectedActionsAvailable } = storeToRefs(authStore);

  // Custom domain filtering: non-owners on custom domains see limited menu
  const { isCustom } = storeToRefs(useProductIdentity());
  const organizationStore = useOrganizationStore();
  const { currentOrganization } = storeToRefs(organizationStore);

  const userRole = computed(() => currentOrganization.value?.current_user_role || null);

  // IDs used for support and testing.
  const orgExtid = computed(() => currentOrganization.value?.extid || null);
  const customerExtid = computed(() => props.cust?.extid || null);

  // Deep link to the active org's audit trail. Two gates on different axes:
  //  - the owner/admin role the /org/:extid route itself requires;
  //  - the instance flag (ORGS_AUDIT_LOGS_ENABLED=false removes the Activity tab
  //    entirely, so the link would dead-end on the Domains panel).
  // The audit_logs ENTITLEMENT is deliberately not a gate: the tab is
  // entitlement-proof — an unentitled org lands on it and sees an upgrade notice
  // instead of being redirected. Mirrors OrganizationSettings.vue.
  const auditLogsFeatureEnabled = computed(() => isOrgsAuditLogsEnabled());

  const orgActivityPath = computed(() =>
    orgExtid.value && auditLogsFeatureEnabled.value ? `/org/${orgExtid.value}/activity` : null
  );

  // Only restrict members on custom domains — admins see the full menu like owners.
  // If org hasn't loaded yet (null role), show full menu to avoid blocking navigation.
  const isCustomDomainMember = computed(
    () => isCustom.value && !!userRole.value && userRole.value === 'member'
  );

  const isElevatedRole = computed(() => userRole.value === 'owner' || userRole.value === 'admin');

  // Match the DomainContext switcher's settings gate: elevated users can edit
  // domains on standalone installs, while billing-enabled installs require the
  // domain-management entitlement when the entitlement list is present.
  const canManageDomainSettings = computed(() => {
    if (!isElevatedRole.value) return false;
    if (!billing_enabled.value) return true;

    const ents = currentOrganization.value?.entitlements;
    return !ents || ents.includes(ENTITLEMENTS.MANAGE_ORG);
  });

  // Reuse the org-switcher visibility logic so the header identity chip appears
  // and disappears in step with the organization context dropdown.
  const { isSoloDefaultContext } = useScopeSwitcherVisibility();

  // Whether the current org carries the manage_orgs entitlement (the same gate
  // the org switcher uses under billing). Absent entitlements → not granted.
  const hasManageOrgs = computed(() => {
    const ents = currentOrganization.value?.entitlements;
    return Array.isArray(ents) && ents.includes(ENTITLEMENTS.MANAGE_ORGS);
  });

  // The identity chip shown next to the domain in the menu header:
  // - 'role'  : the Owner/Admin badge — a real multi-user / multi-org context
  // - 'free'  : a "Free" plan chip linking to billing — a solo user without the
  //             manage_orgs entitlement on a billing-enabled install (plan hint)
  // - 'hidden': nothing — a solo user on a standalone (billing-disabled) install,
  //             so the chip only appears when the org switcher would too
  type IdentityChip = 'role' | 'free' | 'hidden';
  const identityChip = computed<IdentityChip>(() => {
    if (!isElevatedRole.value || props.awaitingMfa) return 'hidden';
    if (!isSoloDefaultContext.value) return 'role';
    if (!billing_enabled.value) return 'hidden';
    return hasManageOrgs.value ? 'role' : 'free';
  });

  // Domain context display in menu header
  const { currentContext, isContextActive } = useDomainContext();
  const showDomainContext = computed(() => isContextActive.value && !props.awaitingMfa);

  const isOpen = ref(false);
  const menuRef = ref<HTMLElement | null>(null);

  // Menu item type
  interface MenuItem {
    id: string;
    to?: string;
    href?: string; // External link
    label: string;
    icon: {
      collection: string;
      name: string;
    };
    variant?: 'default' | 'caution' | 'danger' | 'cta';
    condition?: () => boolean;
    onClick?: () => void | Promise<void>;
    /** Item stays visible but is aria-disabled and non-actionable (#4497). */
    disabled?: () => boolean;
  }

  const openPlanPreviewModal = () => {
    // Do not open a mutation modal while authority is not established
    // (ADR-046#authority-action-gating). The menu item is aria-disabled in that state; this
    // is a belt-and-braces guard.
    if (!protectedActionsAvailable.value) return;
    isPlanPreviewModalOpen.value = true;
    closeMenu();
  };

  const closePlanPreviewModal = () => {
    isPlanPreviewModalOpen.value = false;
  };

  const primaryMenuItems = computed<MenuItem[]>(() => [
    {
      id: 'mfa-verify',
      to: '/mfa-verify',
      label: t('web.auth.complete_mfa_verification'),
      icon: { collection: 'heroicons', name: 'shield-check-solid' },
      variant: 'caution' as const,
      condition: () => props.awaitingMfa,
    },
    {
      id: 'dashboard',
      to: '/dashboard',
      label: t('web.TITLES.dashboard'),
      icon: { collection: 'heroicons', name: 'shield-check-solid' },
      condition: () => !props.awaitingMfa,
    },
    // '/domains' redirects to the active org's domain list (/org/:extid/domains)
    {
      id: 'domains',
      to: '/domains',
      label: t('web.organizations.tabs.domains'),
      icon: { collection: 'heroicons', name: 'globe-alt' },
      condition: () => !props.awaitingMfa,
    },
    {
      id: 'activity',
      to: orgActivityPath.value ?? undefined,
      label: t('web.organizations.tabs.activity'),
      icon: { collection: 'heroicons', name: 'clock' },
      condition: () => !props.awaitingMfa && !!orgActivityPath.value && isElevatedRole.value,
    },
    {
      id: 'billing',
      to: '/billing',
      label: t('web.navigation.billing'),
      icon: { collection: 'heroicons', name: 'credit-card' },
      condition: () => !props.awaitingMfa && !!billing_enabled.value && userRole.value === 'owner',
    },
    {
      id: 'account',
      to: '/account',
      label: t('web.TITLES.account'),
      icon: { collection: 'heroicons', name: 'cog-6-tooth-solid' },
      condition: () => !props.awaitingMfa,
    },
  ]);

  const secondaryMenuItems = computed<MenuItem[]>(() => [
    {
      id: 'colonel',
      href: '/colonel',
      label: t('web.colonel.admin'),
      icon: { collection: 'mdi', name: 'star' },
      condition: () => !props.awaitingMfa && props.colonel && !isCustomDomainMember.value,
    },
    {
      id: 'test-plan',
      label: t('web.colonel.previewPlanMode'),
      icon: { collection: 'heroicons', name: 'beaker' },
      variant: isPreviewModeActive.value ? 'caution' : 'default',
      condition: () => !props.awaitingMfa && props.colonel && !isCustomDomainMember.value,
      // Mutation-issuing (ADR-046#authority-action-gating): opens a modal whose confirm posts
      // /api/colonel/entitlement-preview. Gated on verified authority.
      disabled: () => !protectedActionsAvailable.value,
      onClick: openPlanPreviewModal,
    },
    {
      id: 'help',
      href: 'https://docs.onetimesecret.com',
      label: t('web.TITLES.help'),
      icon: { collection: 'heroicons', name: 'question-mark-circle' },
      condition: () => !props.awaitingMfa,
    },
    {
      id: 'feedback',
      to: '/feedback',
      label: t('web.TITLES.feedback'),
      icon: { collection: 'heroicons', name: 'chat-bubble-bottom-center-text' },
      condition: () => !props.awaitingMfa && !isCustomDomainMember.value,
    },
    {
      id: 'logout',
      label: t('web.COMMON.header_logout'),
      icon: { collection: 'heroicons', name: 'arrow-right-on-rectangle-solid' },
      variant: 'danger' as const,
      onClick: handleLogout,
    },
  ]);

  const menuItems = computed(() => [...primaryMenuItems.value, ...secondaryMenuItems.value]);

  // Visible menu items based on conditions
  const visibleMenuItems = computed(() =>
    menuItems.value.filter((item) => item.condition?.() ?? true)
  );

  // Check if we should show a divider before a menu item
  const shouldShowDividerBefore = (item: MenuItem, index: number): boolean => {
    if (index === 0) return false;

    // Show divider after MFA verification
    if (visibleMenuItems.value[index - 1]?.id === 'mfa-verify') return true;

    // Show divider before upgrade/colonel section
    if (item.id === 'upgrade' || item.id === 'colonel') return true;

    // Show divider before help section
    if (item.id === 'help') return true;

    // Show divider before logout
    if (item.id === 'logout') return true;

    return false;
  };

  // Get CSS classes for menu item based on variant
  const getMenuItemClasses = (variant?: 'default' | 'caution' | 'danger' | 'cta'): string => {
    const baseClasses = 'group flex items-center gap-3 px-4 py-2 text-sm transition-colors';

    switch (variant) {
      case 'caution':
        return `${baseClasses} font-semibold text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20`;
      case 'danger':
        return `${baseClasses} text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20`;
      case 'cta':
        return `${baseClasses} text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-900/20`;
      default:
        return `${baseClasses} text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700`;
    }
  };

  // Get icon classes based on variant and item id
  const getIconClasses = (
    variant?: 'default' | 'caution' | 'danger' | 'cta',
    itemId?: string
  ): string => {
    const baseClasses = 'size-5 transition-colors';

    // Special case for colonel icon
    if (itemId === 'colonel') {
      return `${baseClasses} text-brand-400 group-hover:text-brand-500 dark:text-brand-400 dark:group-hover:text-brand-300`;
    }

    switch (variant) {
      case 'caution':
        return `${baseClasses} text-amber-500 group-hover:text-amber-600 dark:text-amber-400 dark:group-hover:text-amber-300`;
      case 'danger':
        return `${baseClasses} text-red-500 group-hover:text-red-600 dark:text-red-400 dark:group-hover:text-red-300`;
      case 'cta':
        return `${baseClasses} text-brand-500 group-hover:text-brand-600 dark:text-brand-400 dark:group-hover:text-brand-300`;
      default:
        return `${baseClasses} text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300`;
    }
  };

  // Get email from either customer object or direct prop (when awaiting MFA)
  const userEmail = computed(() => props.cust?.email || props.email || '');

  // Display email (CSS truncation handles overflow, no JS truncation needed)
  const displayEmail = computed(() => userEmail.value || 'User');

  // Get user initials for avatar
  const userInitials = computed(() => {
    const email = userEmail.value;
    if (!email) return '?';
    return email.charAt(0).toUpperCase();
  });

  // Toggle dropdown
  const toggleMenu = () => {
    isOpen.value = !isOpen.value;
  };

  // Close dropdown
  const closeMenu = () => {
    isOpen.value = false;
  };

  // Handle logout
  const handleLogout = async () => {
    closeMenu();
    await logout();
  };

  // Handle click outside
  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.value && !menuRef.value.contains(event.target as Node)) {
      closeMenu();
    }
  };

  // Lifecycle hooks
  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<template>
  <div
    ref="menuRef"
    class="relative">
    <!-- Trigger Button -->
    <button
      @click="toggleMenu"
      data-testid="user-menu-trigger"
      class="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-600 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-800 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:outline-none dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white dark:focus:ring-offset-gray-900"
      :aria-expanded="isOpen"
      aria-haspopup="true"
      :aria-label="t('web.COMMON.user_menu')">
      <!-- Avatar Circle with MFA indicator -->
      <div class="relative">
        <div
          :class="[
            'flex size-8 items-center justify-center rounded-full',
            'text-sm font-semibold text-white transition-colors',
            awaitingMfa ? 'bg-amber-500 dark:bg-amber-600' : 'bg-brand-500 dark:bg-brand-600',
          ]">
          {{ userInitials }}
        </div>
        <!-- MFA Pending Badge -->
        <div
          v-if="awaitingMfa"
          class="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-amber-400 ring-2 ring-white dark:bg-amber-300 dark:ring-gray-900"
          :title="t('web.auth.mfa_required')"
          aria-label="MFA verification required"></div>
      </div>

      <!-- Email & Chevron -->
      <div class="hidden items-center gap-1 sm:flex">
        <span
          class="max-w-[140px] truncate lg:max-w-[160px]"
          :title="userEmail"
          >{{ displayEmail }}</span
        >
        <OIcon
          collection="heroicons"
          :name="isOpen ? 'chevron-up-solid' : 'chevron-down-solid'"
          class="size-4 transition-transform"
          aria-hidden="true" />
      </div>

      <!-- Mobile: Just show chevron -->
      <OIcon
        collection="heroicons"
        :name="isOpen ? 'chevron-up-solid' : 'chevron-down-solid'"
        class="size-4 transition-transform sm:hidden"
        aria-hidden="true" />
    </button>

    <!-- Dropdown Menu -->
    <Transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0">
      <div
        v-if="isOpen"
        data-testid="user-menu-dropdown"
        class="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-800 dark:ring-gray-700"
        role="menu"
        :aria-label="t('web.COMMON.user_menu')">
        <!-- User Info Header -->
        <div class="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <!-- Email -->
          <p
            class="truncate text-sm font-medium text-gray-900 dark:text-white"
            :title="cust?.email">
            {{ cust?.email }}
          </p>
          <div
            v-if="!awaitingMfa && customerExtid"
            data-testid="user-menu-customer-extid"
            class="group/extid mt-1 flex min-w-0 items-center gap-1.5">
            <span
              class="truncate font-mono text-[11px] text-gray-400 dark:text-gray-500"
              :title="customerExtid">
              {{ customerExtid }}
            </span>
            <button
              @click.stop="copyToClipboard(customerExtid, 'customerExtid')"
              :title="
                copiedField === 'customerExtid' ? t('web.COMMON.copied') : t('web.COMMON.copy')
              "
              :aria-label="t('web.COMMON.copy')"
              class="shrink-0 rounded p-0.5 text-gray-300 transition-all hover:bg-gray-100 hover:text-gray-500 sm:opacity-0 sm:group-hover/extid:opacity-100 sm:focus:opacity-100 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              :class="{ 'text-green-500 opacity-100': copiedField === 'customerExtid' }">
              <OIcon
                collection="mdi"
                :name="copiedField === 'customerExtid' ? 'check' : 'clipboard-text-outline'"
                class="size-3"
                aria-hidden="true" />
            </button>
          </div>

          <!-- MFA Required Notice -->
          <div
            v-if="awaitingMfa"
            data-testid="user-menu-mfa-badge"
            class="mt-2 flex items-center gap-2 rounded-md px-2 py-1.5">
            <span class="text-sm font-medium">
              {{ t('web.auth.mfa_verification_required') }}
            </span>
          </div>
        </div>

        <!-- Menu Items -->
        <nav
          class="py-1"
          role="navigation">
          <template
            v-for="(item, index) in visibleMenuItems"
            :key="item.id">
            <!-- Divider -->
            <div
              v-if="shouldShowDividerBefore(item, index)"
              class="my-1 border-t border-gray-200 dark:border-gray-700"></div>

            <!-- Router Link Item -->
            <router-link
              v-if="item.to"
              :to="item.to"
              :class="getMenuItemClasses(item.variant)"
              :data-testid="item.id === 'dashboard' ? 'header-dashboard-link' : undefined"
              @click="closeMenu"
              role="menuitem">
              <!-- Special case for upgrade using FancyIcon -->
              <FancyIcon v-if="item.id === 'upgrade'" />
              <OIcon
                v-else
                :collection="item.icon.collection"
                :name="item.icon.name"
                :class="getIconClasses(item.variant, item.id)"
                aria-hidden="true" />
              {{ item.label }}
            </router-link>

            <!-- External Link Item -->
            <a
              v-else-if="item.href"
              :href="item.href"
              target="_blank"
              rel="noopener noreferrer"
              :class="getMenuItemClasses(item.variant)"
              @click="closeMenu"
              role="menuitem">
              <OIcon
                :collection="item.icon.collection"
                :name="item.icon.name"
                :class="getIconClasses(item.variant, item.id)"
                aria-hidden="true" />
              {{ item.label }}
              <OIcon
                collection="heroicons"
                name="arrow-top-right-on-square"
                class="ml-auto size-3.5 text-gray-400"
                aria-hidden="true" />
            </a>

            <!-- Button Item (for logout, test-plan, etc.) -->
            <!-- Logout row with theme toggle -->
            <div
              v-if="item.id === 'logout'"
              class="flex items-center justify-between px-4 py-2">
              <button
                data-testid="user-menu-logout"
                class="group flex items-center gap-3 text-sm text-red-600 transition-colors hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                @click="item.onClick"
                role="menuitem">
                <OIcon
                  :collection="item.icon.collection"
                  :name="item.icon.name"
                  class="size-5 text-red-500 transition-colors group-hover:text-red-600 dark:text-red-400 dark:group-hover:text-red-300"
                  aria-hidden="true" />
                {{ item.label }}
              </button>
              <!-- Theme Toggle -->
              <button
                @click.stop="toggleDarkMode"
                data-testid="user-menu-theme-toggle"
                :aria-label="t('web.layout.toggle_dark_mode')"
                :aria-pressed="isDarkMode"
                :title="
                  isDarkMode
                    ? t('web.layout.switch_to_blank_mode', ['light'])
                    : t('web.layout.switch_to_blank_mode', ['dark'])
                "
                class="flex size-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300">
                <OIcon
                  collection="ph"
                  :name="isDarkMode ? 'moon' : 'sun'"
                  class="size-4"
                  aria-hidden="true" />
              </button>
            </div>
            <!-- Other button items -->
            <button
              v-else-if="item.onClick"
              :data-testid="item.id === 'test-plan' ? 'user-menu-test-plan' : undefined"
              :class="[
                getMenuItemClasses(item.variant),
                'w-full',
                item.disabled?.() ? 'cursor-not-allowed opacity-50' : '',
              ]"
              :disabled="item.disabled?.() ?? false"
              :aria-disabled="item.disabled?.() ?? false"
              @click="item.disabled?.() ? undefined : item.onClick?.()"
              role="menuitem">
              <OIcon
                :collection="item.icon.collection"
                :name="item.icon.name"
                :class="getIconClasses(item.variant, item.id)"
                aria-hidden="true" />
              {{ item.label }}
            </button>
          </template>
        </nav>

        <!-- Context Footer: selected domain and account role/plan -->
        <div
          v-if="!awaitingMfa && (showDomainContext || identityChip !== 'hidden')"
          class="border-t border-gray-200 bg-gray-50/80 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
          <div
            v-if="showDomainContext || identityChip !== 'hidden'"
            class="flex min-w-0 items-center gap-2">
            <div
              v-if="showDomainContext"
              class="group/domain flex min-w-0 flex-1 items-center gap-1.5">
              <span
                class="text-xs font-medium break-all text-gray-600 dark:text-gray-300"
                :title="currentContext.displayName">
                {{ currentContext.displayName }}
              </span>
              <router-link
                v-if="currentContext.extid && orgExtid && canManageDomainSettings"
                :to="`/org/${orgExtid}/domains/${currentContext.extid}`"
                :title="t('web.domains.domain_settings')"
                :aria-label="t('web.domains.domain_settings')"
                @click="closeMenu"
                class="shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300">
                <OIcon
                  collection="heroicons"
                  name="cog"
                  class="size-4"
                  aria-hidden="true" />
              </router-link>
            </div>
            <router-link
              v-if="identityChip === 'role'"
              :to="orgExtid ? `/org/${orgExtid}` : '/domains'"
              data-testid="user-menu-role-badge"
              :title="t('web.organizations.tabs.domains')"
              @click="closeMenu"
              :class="[
                'inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase shadow-xs transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                userRole === 'owner'
                  ? 'border-brand-200/60 bg-brand-50 text-brand-600 hover:border-brand-300 hover:bg-brand-100 dark:border-brand-700/40 dark:bg-brand-950/50 dark:text-brand-400 dark:hover:bg-brand-900/40'
                  : 'border-violet-200/60 bg-violet-50 text-violet-600 hover:border-violet-300 hover:bg-violet-100 dark:border-violet-700/40 dark:bg-violet-950/50 dark:text-violet-400 dark:hover:bg-violet-900/40',
              ]">
              <span
                :class="[
                  'size-1.5 rounded-full',
                  userRole === 'owner'
                    ? 'bg-brand-500 dark:bg-brand-400'
                    : 'bg-violet-500 dark:bg-violet-400',
                ]"
                aria-hidden="true"></span>
              {{ t(`web.organizations.members.roles.${userRole}`) }}
            </router-link>
            <router-link
              v-else-if="identityChip === 'free'"
              to="/billing"
              data-testid="user-menu-plan-chip"
              :title="t('web.navigation.billing')"
              @click="closeMenu"
              class="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-200/70 bg-white px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-gray-500 uppercase shadow-xs transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600 dark:border-gray-600/50 dark:bg-gray-700/40 dark:text-gray-400 dark:hover:border-brand-700/50 dark:hover:bg-brand-950/40 dark:hover:text-brand-400">
              <span
                class="size-1.5 rounded-full bg-gray-400 dark:bg-gray-500"
                aria-hidden="true"></span>
              {{ t('web.billing.plans.free_plan') }}
            </router-link>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Plan Test Modal -->
    <PlanPreviewModal
      :is-open="isPlanPreviewModalOpen"
      @close="closePlanPreviewModal" />
  </div>
</template>
